/** See `src/i18n/locales/en/overview.ts`. */
export const overview = {
  page: {
    title: 'Overzicht',
    lead: 'Hoe gaat het met {{company}}?',
    noScope: 'Kies een bedrijf om het overzicht te zien.',
    noDepartments: 'Geen enkele afdeling hier heeft cijfers die je mag zien.',
    disclaimer:
      'Alle waarden zijn indicatieve welzijnsinzichten op groepsniveau en niet bedoeld als medische diagnose.',
  },
  summary: {
    caption: 'AI-samenvatting op basis van geanonimiseerde, geaggregeerde data.',
  },
  figures: {
    vitality: 'Vitaliteit',
    stress: 'Stress',
    sleep: 'Slaap',
    engagement: 'Betrokkenheid',
    activeParticipants: 'Actieve deelnemers',
  },
  figuresInfo: {
    vitality: 'Samengestelde score op basis van slaap, activiteit, HRV, stress en hartslag.',
    stress: 'Gemiddeld stressniveau, direct gemeten via de wearable.',
    sleep: 'Gemiddelde slaapduur per nacht, direct gemeten via de wearable.',
    engagement: 'Aandeel medewerkers dat actief de app gebruikt.',
    activeParticipants: 'Aantal medewerkers dat de afgelopen periode actief was.',
  },
  legend: {
    title: 'Wat je ziet',
    measured:
      'Direct gemeten — rechtstreeks afkomstig uit de NOLATR-wearable of een gekoppelde sensor. Hoogste betrouwbaarheid.',
    aiDerived:
      'AI-afgeleid — geen directe meting. De AI combineert meerdere signalen tot één indicatie.',
    usage: 'Gebruik & gedrag — feitelijke interacties in de app, geen gezondheidsmeting.',
  },
  developments: {
    title: 'Belangrijkste ontwikkelingen',
    empty: 'Geen bijzondere ontwikkelingen deze periode.',
    stress: {
      title: 'Kans om rust te bieden (stress verhoogd)',
      body: 'Een vriendelijke rustmoment of ademhalings-tip kan het team helpen opladen.',
    },
    sleep: {
      title: 'Kans: slaap onder het doel',
      body: 'Een gerichte, positieve hersteltip kan het team goed ondersteunen.',
    },
    vitalityGood: {
      title: 'De vitaliteit ontwikkelt zich goed',
      body: 'Het team houdt het huidige ritme goed vast — een mooi moment om dit te erkennen.',
    },
    needsReactivation: {
      title: '{{count}} personen moeten opnieuw geactiveerd worden',
      body: 'Een korte, persoonlijke heractivatie-boodschap werkt vaak het best.',
    },
    mostActiveWeekday: {
      title: 'Meeste energie op {{weekday}}',
      body: 'Plan challenges en communicatie rond de meest actieve dag voor maximaal bereik.',
    },
    challengeParticipation: {
      title: 'Challenge-deelname {{pct}}%',
      body: 'Een nieuwe, laagdrempelige teamchallenge kan meer mensen laten meedoen.',
    },
  },
  actions: {
    title: 'Acties voor HR',
    empty: 'Op dit moment geen aanbevolen acties.',
    sendReminder: {
      title: '{{count}} medewerkers nog niet geactiveerd',
      subtitle: 'Stuur een herinnering',
    },
    reactivate: {
      title: '{{count}} medewerkers moeten opnieuw geactiveerd worden',
      subtitle: 'Neem contact op',
    },
    stressCheckIn: {
      title: 'Verhoogde stress binnen het team',
      subtitle: 'Overweeg een korte check-in over de werkdruk',
    },
    sleepReminder: {
      title: 'Slaap onder het doel binnen het team',
      subtitle: 'Overweeg een herinnering over de avondroutine en schermtijd',
    },
    activeChallenge: {
      title: "'{{name}}' loopt — plan de volgende",
      subtitle: 'Challenges',
    },
    pilotEvaluation: {
      interim: {
        title: "Pilot '{{name}}' is klaar voor tussentijdse evaluatie",
        subtitle: 'Bekijk pilot',
      },
      final: {
        title: "Pilot '{{name}}' is klaar voor eindevaluatie",
        subtitle: 'Bekijk pilot',
      },
    },
  },
  energy: {
    title: 'Energie & werkbeleving',
    subtitle:
      'Hoe beleeft het team de werkdag — energie, pauzes, werkritme en betrokkenheid. Alleen geaggregeerd, nooit individueel.',
    badge: 'Geaggregeerd & geanonimiseerd',
    tbd: 'Nader te bepalen',
    missingData: {
      noSource: 'Energie-index en eNPS: nog geen databron bepaald.',
      insufficientParticipants: 'Onvoldoende deelnemers voor {{metrics}}.',
    },
    privacyNote:
      'Ondersteunende inzichten, geen monitoring. Indicatief welzijn op groepsniveau — geen medische duiding.',
    metrics: {
      energyIndex: 'Energie-index',
      breakAdoption: 'Pauze-adoptie',
      focusRhythm: 'Focusritme',
      coachAdoption: 'Coach-adoptie',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Pilotstatus',
    empty: 'Nog geen pilots.',
    participantsAndDay: '{{count}} deelnemers · dag {{day}} van {{total}}',
    viewReport: 'Bekijk pilotrapport',
  },
  challenges: {
    title: 'Actieve challenges',
    empty: 'Nog geen challenges.',
    active: 'Actief',
    total: 'Totaal',
    participants: 'Deelnemers',
    activeBadge: '{{count}} actief',
    manageLink: 'Beheer challenges',
    noneActive: 'Geen actieve challenges op dit moment.',
    participantsCount: '{{count}} deelnemers',
  },
  trends: {
    title: 'Trends',
    filters: {
      allDepartments: 'Alle afdelingen',
      participants: {
        label: 'Deelnemers',
        all: 'Alle deelnemers',
        manager: 'Leidinggevenden',
        employee: 'Medewerkers',
      },
    },
    vitalityScore: 'Vitaliteitsscore over tijd',
    activeUsersPerWeek: 'Actieve gebruikers per week',
    sleep: 'Slaaptrend',
    stress: 'Stresstrend',
    hrv: 'HRV-trend',
    recovery: 'Herstel-trend',
    activity: 'Activiteitstrend',
    notAvailable: 'Nog geen databron voor deze trend — niet beschikbaar in deze release.',
    vsPreviousPeriod: 'vs vorige periode',
    info: {
      vitalityScore:
        'Samengestelde vitaliteitsscore per dag, op basis van alle vitaliteitssignalen.',
      activeUsersPerWeek: 'Aantal unieke medewerkers dat per week actief de app gebruikte.',
      sleep: 'Gemiddelde slaapduur per dag, direct gemeten via de wearable.',
      stress: 'Gemiddeld stressniveau per dag, direct gemeten via de wearable.',
      hrv: 'Gemiddelde hartslagvariabiliteit (HRV) per dag, een indicator voor herstel.',
      recovery: 'Samengestelde herstelscore per dag, op basis van slaap, HRV en activiteit.',
      activity: 'Gemiddeld aantal stappen per dag, direct gemeten via de wearable.',
    },
  },
} as const;
